import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { AppSetting, AppSettingHistoryEntry, AppSettingUpdateRequest } from '../../_models/admin/app-setting.models';

/**
 * Typed calls to the admin-only settings store (api/Maintenance/Settings...). Same BaseService pattern as
 * FeatureSwitchService: prepareOptions() hashes the exact body sent, and responses are unwrapped.
 */
@Injectable({ providedIn: 'root' })
export class AppSettingsService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public getSettings(): Observable<AppSetting[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/Settings', this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as AppSetting[]),
      catchError(this.handleError('getSettings'))
    );
  }

  /** Saves one setting's value (always a string on the wire) and returns the setting as it now stands. */
  public saveSetting(settingKey: string, value: string): Observable<AppSetting> {
    const body: AppSettingUpdateRequest = { value };
    return this.http.put(
      this.apiUrl + 'api/Maintenance/Settings/' + encodeURIComponent(settingKey),
      body,
      this.prepareOptions(body)
    ).pipe(
      map((data: any) => this.unwrap(data) as AppSetting),
      catchError(this.handleError('saveSetting'))
    );
  }

  /** Sets one brand's value for a setting (a per-brand override) and returns the setting with its overrides. */
  public saveBrandOverride(settingKey: string, companyId: number, value: string): Observable<AppSetting> {
    const body: AppSettingUpdateRequest = { value };
    return this.http.put(
      this.apiUrl + 'api/Maintenance/Settings/' + encodeURIComponent(settingKey) + '/brands/' + companyId,
      body,
      this.prepareOptions(body)
    ).pipe(
      map((data: any) => this.unwrap(data) as AppSetting),
      catchError(this.handleError('saveSettingBrandOverride'))
    );
  }

  /** "Collapse to one value": deletes every brand override so every brand uses the global value. */
  public collapse(settingKey: string): Observable<AppSetting> {
    const body = {};
    return this.http.post(
      this.apiUrl + 'api/Maintenance/Settings/' + encodeURIComponent(settingKey) + '/collapse',
      body,
      this.prepareOptions(body)
    ).pipe(
      map((data: any) => this.unwrap(data) as AppSetting),
      catchError(this.handleError('collapseSetting'))
    );
  }

  /** The latest 50 changes to one setting and its brand overrides, newest first. */
  public getHistory(settingKey: string): Observable<AppSettingHistoryEntry[]> {
    return this.http.get(
      this.apiUrl + 'api/Maintenance/Settings/' + encodeURIComponent(settingKey) + '/history',
      this.prepareOptions(null)
    ).pipe(
      map((data: any) => (this.unwrap(data) || []) as AppSettingHistoryEntry[]),
      catchError(this.handleError('getSettingHistory'))
    );
  }

  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
